export interface LessonPlanItem {
  id: number;
  name: string;
  slug?: string | null;
}

export interface FeaturedLessonPlanItem extends LessonPlanItem {
    theme_lesson_plans: { featured_on: string | null; purpose?: string | number };
}

export interface LessonPlanCellProps {
  lessonPlans: LessonPlanItem[];
  featuredLessonPlans?: FeaturedLessonPlanItem[];
}

export const normalizeThemeLessonPlans = (theme: any): any[] =>
    [...((theme?.theme_lesson_plans ?? []) as any[])]
      .sort((a, b) => {
        const aDate = a.featured_on ?? '';
        const bDate = b.featured_on ?? '';
        if (aDate && bDate) return aDate.localeCompare(bDate);
        if (aDate) return -1;
        if (bDate) return 1;
        return a.id - b.id;
      })
      .map((tlp) => ({
        ...tlp.lesson_plan,
        theme_lesson_plans: { featured_on: tlp.featured_on, purpose: tlp.purpose }
      }));


const weekKeyOf = (isoDate: string): string => {
    const d = new Date(`${isoDate.slice(0, 10)}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
    return d.toISOString().slice(0, 10);
};
const isFirstClassPlan = (t: FeaturedLessonPlanItem) => {
    const p = t.theme_lesson_plans.purpose as unknown;
    return p === 'first_class' || p === 1 || p === '1';
};

/**
 * Mirrors sticky-fingers-api-3/src/utils/lessonPlanVirtualizer.ts.
 * Any change here must be made there. Assigns plans to the class dates of a
 * SINGLE session; callers must not pass dates from more than one course.
 *
 * A  the week containing this session's first class takes the most recent
 *    purpose=1 recipe on or before that week. A session opening before its own
 *    month's first-class anchor picks up the previous month's. Fires once.
 * B  every other week takes the most recent dated anchor on or before it.
 *    All anchors share one timeline regardless of purpose.
 * C  weeks earlier than the first dated anchor draw from the alphabetical
 *    any-time pool, one plan per week, reused by every class in that week.
 * D  otherwise absent from the map.
 */
export const buildVirtualAssignments = (
    classDates: string[],
    plans: FeaturedLessonPlanItem[],
): Map<string, FeaturedLessonPlanItem> => {
    const featuredOn = (p: FeaturedLessonPlanItem) => p.theme_lesson_plans?.featured_on ?? null;

    // First-class anchors carry forward on their own timeline, so the opening
    // week can fall back to an earlier month when this month's has not happened.
    const firstClassAnchors = plans
        .filter((p) => isFirstClassPlan(p) && featuredOn(p))
        .map((p) => ({ wk: weekKeyOf(featuredOn(p) as string), plan: p }))
        .sort((a, b) => a.wk.localeCompare(b.wk));

    // One timeline for rule B. purpose does not partition this list.
    const datedAnchors = plans
        .filter((p) => featuredOn(p))
        .map((p) => ({ wk: weekKeyOf(featuredOn(p) as string), plan: p }))
        .sort((a, b) => a.wk.localeCompare(b.wk));

    // Dateless anchors of any purpose fall into the pool, including purpose=1.
    const anyTime = plans
        .filter((p) => !featuredOn(p))
        .sort((a, b) => a.name.localeCompare(b.name));

    const onOrBefore = (
        list: Array<{ wk: string; plan: FeaturedLessonPlanItem }>,
        wk: string,
    ): FeaturedLessonPlanItem | undefined => {
        let chosen: FeaturedLessonPlanItem | undefined;
        for (const a of list) { if (a.wk <= wk) chosen = a.plan; else break; }
        return chosen;
    };

    const days = Array.from(new Set(classDates.map((d) => (d ?? '').slice(0, 10))))
        .filter(Boolean)
        .sort();

    // Rule A. The week holding this session's first class, whether or not that
    // week sits wholly inside one month.
    const openingWeek = days.length ? weekKeyOf(days[0]) : undefined;
    const openingPlan = openingWeek ? onOrBefore(firstClassAnchors, openingWeek) : undefined;

    const anyTimeByWeek = new Map<string, FeaturedLessonPlanItem>();
    let fill = 0;
    const out = new Map<string, FeaturedLessonPlanItem>();

    days.forEach((day) => {
        const wk = weekKeyOf(day);

        if (openingPlan && wk === openingWeek) { out.set(day, openingPlan); return; }

        const dated = onOrBefore(datedAnchors, wk);
        if (dated) { out.set(day, dated); return; }

        const claimed = anyTimeByWeek.get(wk);
        if (claimed) { out.set(day, claimed); return; }

        if (fill < anyTime.length) {
            out.set(day, anyTime[fill]);
            anyTimeByWeek.set(wk, anyTime[fill]);
            fill++;
        }
    });

    return out;
};

export const getLessonPlanCellProps = (
    lessonPlans: LessonPlanItem[],
    virtualPlan?: FeaturedLessonPlanItem | null,
) => {
    if (lessonPlans.length > 0) {
        return { lessonPlans, featuredLessonPlans: undefined };
    }
    return { lessonPlans, featuredLessonPlans: virtualPlan ? [virtualPlan] : [] };
};
